// src/components/admin/Category/PricingTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
//import { useParams } from 'react-router-dom'; // If category ID comes from URL params and not just props
import api from '../../../services/apiServices'; // Your axios instance
import { Button } from '../../../components/ui/button'; // Your Button component
import CategoryPricingSettingsForm from './Pricing/CategoryPricingSettingsForm';
import PricingRulesSection from './Pricing/PricingRulesSection'; // New component for rules
import { useToast } from '../../../hooks/use-toast'; 
//import { useAdminStore } from '../../../store/adminStore'; 
import MasterPricingDefaultForm from './Pricing/MasterPricingDefaultForm'; // <-- NEW IMPORT

// Helper to structure API calls (you can put this in apiServices.js or a new pricingApi.js)
const pricingApi = {
    getCategorySettings: (categoryId, locale) => api.get(`/admin/lead-pricing/${categoryId}/settings/`, { params: { locale } }),
    createOrUpdateCategorySettings: (categoryId, settingsData, locale) => api.put(`/admin/lead-pricing/${categoryId}/settings/`, settingsData, { params: { locale } }),
    getMasterDefault: (masterCategoryId) => api.get(`/admin/lead-pricing/master-defaults/${masterCategoryId}`),
    createOrUpdateMasterDefault: (masterCategoryId, settingsData) => api.put(`/admin/lead-pricing/master-defaults/${masterCategoryId}`, settingsData),
    // Rule and Condition Definition APIs will go here later
};

export default function PricingTab({ category, questionList, locale }) { // category prop is passed from CategoryTabs
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditingSettings, setIsEditingSettings] = useState(false);
    //const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT
    const isMasterLocale = locale === 'master'
    const { toast } = useToast();

    console.log(`Category Category:${category.name} and qnas are:`,questionList)
    // const { categoryId } = useParams(); // Or use category.id directly
    const categoryId = category?.id;

    const fetchSettings = useCallback(async () => {
        if (!categoryId) {
            setIsLoading(false);
            setSettings(null); // No category selected or category has no ID
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            let response;
            if (isMasterLocale) {
                // Fetch Master Default
                response = await pricingApi.getMasterDefault(categoryId);
            } else {
                // Fetch Localized Settings
                response = await pricingApi.getCategorySettings(categoryId, locale);
            }
            setSettings(response.data);
            setIsEditingSettings(false);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                setSettings(null); // No settings found, allow creation
                setIsEditingSettings(true); // Default to editing mode to create new settings
            } else {
                setError(`Failed to fetch pricing settings: ${err.message}`);
                console.error("Fetch settings error:", err);
                toast({ // Use toast for info
                    variant: "default",
                    title: isMasterLocale ? "No Default Price Set" : "No Local Pricing Set",
                    description: isMasterLocale ? "Please set the default base price." : "Please configure the pricing settings.",
                 });
            }
        } finally {
            setIsLoading(false);
        }
    },[categoryId, locale, isMasterLocale]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSettingsSave = async (formData) => {
        if (!categoryId) return;
        setIsLoading(true);
        setError('');
        try {
            let response;
            if (isMasterLocale) {
                // Save Master Default (formData only has default_base_price_coins)
                 console.log(`Saving master default for category ${categoryId} with data: ${JSON.stringify(formData)}`);
                 response = await pricingApi.createOrUpdateMasterDefault(categoryId, formData);
                 toast({ title: "Success!", description: "Master default price saved." });
            } else {
                // Save Localized Settings
                console.log(`Saving localized settings for category ${categoryId} (locale: ${locale}) with data: ${JSON.stringify(formData)}`);
                response = await pricingApi.createOrUpdateCategorySettings(categoryId, formData, locale);
                toast({ title: "Success!", description: "Localized pricing settings saved." });
            }
            setSettings(response.data);
            setIsEditingSettings(false);
        } catch (err) {
            const errorMsg = `Failed to save ${isMasterLocale ? 'master default' : 'pricing settings'}: ${err.response?.data?.detail || err.message}`;
            console.error("Save settings error:", err);
            toast({ variant: "destructive", title: "Save Failed", description: errorMsg });
        } finally {
            setIsLoading(false);
        }
    };

    if (!category) {
        return <p className="text-gray-600">Please select a category first.</p>;
    }
    if (isLoading && !settings && !error) {
        return <p className="text-muted-foreground">Loading pricing configuration...</p>; // Use theme color
    }
    if (error && !isLoading) { // Don't show error during retry loading
        return (
            <div className="text-destructive"> {/* Use theme color */}
                <p>{error}</p>
                <Button onClick={fetchSettings} variant="outline" className="mt-2">Retry</Button>
            </div>  
        );
    }

    const SettingsComponent = isMasterLocale ? MasterPricingDefaultForm : CategoryPricingSettingsForm;
    const settingsTitle = isMasterLocale ? "Master Default Price" : "Base Pricing & Boundaries";

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold text-foreground">Pricing Configuration for: {category.name}</h2>

            {/* Section for Settings (Master or Localized) */}
            <div className="p-4 border rounded-lg shadow-sm bg-card"> {/* Use theme vars */}
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-medium text-card-foreground">{settingsTitle}</h3>
                    {!isEditingSettings && settings && (
                        <Button variant="outline" size="sm" onClick={() => setIsEditingSettings(true)}>
                            Edit
                        </Button>
                    )}
                </div>

                {isEditingSettings || !settings ? (
                    // Render the appropriate form
                    <SettingsComponent
                        initialData={settings}
                        onSave={handleSettingsSave}
                        onCancel={() => {
                            setIsEditingSettings(false);
                            // Only refetch if cancelling a "new" form where settings were previously null
                            if (!settings) fetchSettings();
                        }}
                        isLoading={isLoading}
                    />
                ) : (
                    // Display mode
                    settings && (
                        <div className="space-y-2 text-sm text-muted-foreground"> {/* Use theme colors */}
                            {isMasterLocale ? (
                                <p><strong>Default Base Price:</strong> {settings.default_base_price_coins} coins</p>
                            ) : (
                                <>
                                    <p><strong>Base Price:</strong> {settings.base_price_coins} coins</p>
                                    <p><strong>Min Price:</strong> {settings.min_price_coins ?? 'Not set'} coins</p>
                                    <p><strong>Max Price:</strong> {settings.max_price_coins ?? 'Not set'} coins</p>
                                    <p><strong>Pricing Active:</strong> {settings.is_pricing_active ? 'Yes' : 'No'}</p>
                                    <p><strong>Variable (Rule-based) Pricing Active:</strong> {settings.is_variable_pricing_active ? 'Yes' : 'No'}</p>
                                    <p><strong>Time-Delay Discount:</strong> {settings.time_reduction_config_enabled ? 'Enabled' : 'Disabled'}</p>
                                    {settings.time_reduction_config_enabled && (
                                        <p className="pl-4">
                                            <strong>{settings.time_discount_label || 'Discount'} — After {settings.time_discount_delay_hours ?? '?'}h:</strong>{' '}
                                            {settings.time_discount_modifier_type === 'PERCENTAGE'
                                                ? `${settings.time_discount_value_percentage_rate != null ? (parseFloat(settings.time_discount_value_percentage_rate) * 100) : '?'}% off`
                                                : settings.time_discount_modifier_type === 'ABSOLUTE'
                                                    ? `${settings.time_discount_value_absolute ?? '?'} coins off`
                                                    : 'not configured'}
                                        </p>
                                    )}
                                    <p><strong>Interaction Pricing Enabled (config):</strong> {settings.interaction_pricing_config_enabled ? 'Yes' : 'No'}</p>
                                </>
                            )}
                        </div>
                    )
                )}
                 {/* This handles the case where loading finished, no settings found, and user hasn't clicked Edit yet */}
                 {!settings && !isLoading && !isEditingSettings && (
                     <p className="text-muted-foreground">
                         No {isMasterLocale ? 'default price' : 'pricing settings'} found.
                         <Button variant="link" onClick={() => setIsEditingSettings(true)} className="pl-1">
                             {isMasterLocale ? 'Set Default Price' : 'Setup Pricing'}
                         </Button>
                     </p>
                )}
            </div>

            {/* Section for Pricing Rules (Only for Localized and if settings exist) */}
            {!isMasterLocale && settings && settings.id && (
                 <PricingRulesSection
                    categorySettingId={settings.id}
                    categoryId={categoryId}
                    questionList={questionList}
                    canEditRules={settings.is_variable_pricing_active}
                />
            )}
             {/* Message if rules cannot be shown */}
             {!isMasterLocale && !settings && !isLoading && !isEditingSettings && (
                <div className="mt-4 p-4 border-t">
                    <p className="text-muted-foreground">Please set up and save base pricing settings first to manage pricing rules.</p>
                </div>
            )}
        </div>
    );
}